import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  where,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'artMovements';
const MOVEMENT_CACHE_TTL_MS = 10 * 60 * 1000;
const movementByNameCache = new Map();

function normalizeMovementName(name = '') {
  return String(name || '').trim().toLowerCase();
}

function movementDocIdFromName(name = '') {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[\\/]+/g, '-')
    .replace(/[^a-z0-9()\\s-]/g, '')
    .replace(/\\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function splitCharacteristics(text = '') {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  return clean
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 5);
}

async function fetchMovementFromWikipedia(name) {
  const query = encodeURIComponent(String(name || '').trim());
  if (!query) return null;
  try {
    const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${query}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const description = String(data?.extract || '').trim();
    if (!description) return null;
    return {
      name: String(name || '').trim(),
      description,
      characteristics: splitCharacteristics(description),
      timePeriod: String(data?.description || '').trim(),
      famousArtists: [],
      techniques: [],
      source: 'wikipedia',
      sourceUrl: data?.content_urls?.desktop?.page || '',
      fetchedAt: new Date(),
    };
  } catch (_) {
    return null;
  }
}

// Get all art movements
export const getAllArtMovements = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('name', 'asc'));
    const querySnapshot = await getDocs(q);
    const artMovements = [];
    
    querySnapshot.forEach((doc) => {
      artMovements.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return artMovements;
  } catch (error) {
    console.error('Error getting art movements:', error);
    throw error;
  }
};

// Get a single art movement by ID
export const getArtMovement = async (id) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Art movement not found');
    }
  } catch (error) {
    console.error('Error getting art movement:', error);
    throw error;
  }
};

// Add a new art movement
export const addArtMovement = async (artMovementData) => {
  try {
    // Keep doc IDs path-safe even when movement names contain slashes.
    const safeDocId = movementDocIdFromName(artMovementData?.name || '');
    const docRef = doc(db, COLLECTION_NAME, safeDocId || String(artMovementData?.name || '').trim());
    await setDoc(docRef, {
      ...artMovementData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return artMovementData.name;
  } catch (error) {
    console.error('Error adding art movement:', error);
    throw error;
  }
};

// Update an existing art movement
export const updateArtMovement = async (id, artMovementData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...artMovementData,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating art movement:', error);
    throw error;
  }
};

// Delete an art movement
export const deleteArtMovement = async (id) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
    
    return true;
  } catch (error) {
    console.error('Error deleting art movement:', error);
    throw error;
  }
};

// Get art movement by name (for the popup component)
export const getArtMovementByName = async (name) => {
  try {
    const normalized = normalizeMovementName(name);
    if (!normalized) return null;

    const cached = movementByNameCache.get(normalized);
    if (cached && Date.now() - cached.ts < MOVEMENT_CACHE_TTL_MS) {
      return cached.data;
    }

    const docRef = doc(db, COLLECTION_NAME, name);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      movementByNameCache.set(normalized, { data, ts: Date.now() });
      return data;
    } else {
      // Try path-safe slug id (for names with '/', etc.).
      const safeDocId = movementDocIdFromName(name);
      if (safeDocId && safeDocId !== name) {
        const safeSnap = await getDoc(doc(db, COLLECTION_NAME, safeDocId));
        if (safeSnap.exists()) {
          const data = safeSnap.data();
          movementByNameCache.set(normalized, { data, ts: Date.now() });
          return data;
        }
      }

      // Fallback query by exact display name field.
      const byNameQ = query(collection(db, COLLECTION_NAME), where('name', '==', String(name || '').trim()), limit(1));
      const byNameSnap = await getDocs(byNameQ);
      if (!byNameSnap.empty) {
        const data = byNameSnap.docs[0].data();
        movementByNameCache.set(normalized, { data, ts: Date.now() });
        return data;
      }

      // Auto-fallback so new movements still have content in Art History "Start analysis" popup.
      const wikiData = await fetchMovementFromWikipedia(name);
      if (wikiData) {
        movementByNameCache.set(normalized, { data: wikiData, ts: Date.now() });
        return wikiData;
      }
      return null;
    }
  } catch (error) {
    console.error('Error getting art movement by name:', error);
    return null;
  }
};
